import { create } from 'zustand';
import { notificationApi, Notification } from '../utils/notificationApi';

interface NotificationStore {
  notifications: Notification[];
  unreadCount: number;
  isDropdownOpen: boolean;
  isBannerVisible: boolean;
  latestNotification: Notification | null;
  
  fetchNotifications: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: number) => void;
  toggleDropdown: () => void;
  showBanner: (notification: Notification) => void;
  hideBanner: () => void;
}

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isDropdownOpen: false,
  isBannerVisible: false,
  latestNotification: null,

  fetchNotifications: async () => {
    try {
      const result = await notificationApi.getNotifications();
      if (result.success) {
        set({ notifications: result.data });
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  },

  fetchUnreadCount: async () => {
    try {
      const result = await notificationApi.getUnreadCount();
      if (result.success) {
        set((state) => {
          const previousCount = state.unreadCount;
          const newCount = result.data.count;
          
          if (newCount > previousCount) {
            get().fetchNotificationsWithBanner();
          }
          
          return { unreadCount: newCount };
        });
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  },

  fetchNotificationsWithBanner: async () => {
    try {
      const result = await notificationApi.getNotifications();
      if (result.success && result.data.length > 0) {
        const latest = result.data[0];
        set({ notifications: result.data });
        get().showBanner(latest);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  },

  markAsRead: async (id: number) => {
    try {
      await notificationApi.markAsRead(id);
      set((state) => ({
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, is_read: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  },

  markAllAsRead: async () => {
    try {
      await notificationApi.markAllAsRead();
      set((state) => ({
        notifications: state.notifications.map((n) => ({ ...n, is_read: true })),
        unreadCount: 0,
      }));
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  },

  deleteNotification: (id: number) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  },

  toggleDropdown: () => {
    set((state) => ({ isDropdownOpen: !state.isDropdownOpen }));
    if (!get().isDropdownOpen) {
      get().markAllAsRead();
    }
  },

  showBanner: (notification: Notification) => {
    set({ 
      latestNotification: notification, 
      isBannerVisible: true 
    });
    setTimeout(() => {
      set({ isBannerVisible: false });
    }, 5000);
  },

  hideBanner: () => {
    set({ isBannerVisible: false });
  },
}));

export default useNotificationStore;